import { XIcon } from 'lucide-react';
import { Button, Card, CardAction, CardContent, CardHeader, Tooltip, TooltipContent, TooltipTrigger } from 'shared-ui';

import { AIR_VALVE_ICON_URL, HIGH_RISK_AIR_VALVE_ICON_URL } from '../utils/airValveMarkers';

export interface SaviMapKeyCardProps {
    isOpen: boolean;
    onClose: () => void;
    /** Number of high-risk air valves for the selected client, shown next to the legend row. */
    highRiskCount: number | null;
}

function KeyRow({ icon, label }: { icon: string; label: string }) {
    return (
        <div className="flex items-center gap-3 py-1.5">
            <img src={icon} alt="" aria-hidden="true" className="h-[3.25rem] w-[2.625rem] shrink-0" />
            <span className="text-muted-foreground text-sm leading-5 font-normal tracking-normal">{label}</span>
        </div>
    );
}

/**
 * Floating legend for SAVI mode. Anchored to the top-right corner of the map, just left of the
 * control column. Opened/closed from the Info map control; dismissed only via the close button or
 * by toggling that control (no click-outside) — see MapView's exclusive-panel coordination.
 */
export function SaviMapKeyCard({ isOpen, onClose, highRiskCount }: SaviMapKeyCardProps) {
    if (!isOpen) return null;

    const highRiskLabel =
        highRiskCount != null ? `High Risk Air Valve (${String(highRiskCount)})` : 'High Risk Air Valve';

    return (
        <Card className="pointer-events-auto absolute top-2 right-2 z-10 h-[16rem] w-[18.75rem] rounded-md py-6">
            <CardHeader className="px-6">
                <div className="space-y-0.5">
                    <h2 className="text-sm leading-5 font-black tracking-normal">SAVI™</h2>
                    <p className="text-muted-foreground text-sm leading-4 font-medium">Map Key</p>
                </div>
                <CardAction>
                    {/* Matches the upsell dialog/drawer close button: Origin-blue glyph, 1px token
                        border, secondary hover. A plain Card has no dialog/drawer-close data-slot,
                        so the look is reproduced with call-site Tailwind tokens. */}
                    <Tooltip>
                        <TooltipTrigger
                            render={
                                <Button
                                    variant="ghost"
                                    size="icon-sm"
                                    onClick={onClose}
                                    aria-label="Close SAVI map key"
                                    className="text-origin-blue border-border hover:bg-secondary hover:text-origin-blue size-10 shrink-0 rounded-md border"
                                >
                                    <XIcon className="size-4" strokeWidth={2.25} />
                                </Button>
                            }
                        />
                        <TooltipContent side="top">Close</TooltipContent>
                    </Tooltip>
                </CardAction>
            </CardHeader>
            {/* Inset divider under the header — gaps each side rather than full-bleed. */}
            <div className="border-border mx-6 border-t" />
            <CardContent className="px-6">
                <KeyRow icon={HIGH_RISK_AIR_VALVE_ICON_URL} label={highRiskLabel} />
                <KeyRow icon={AIR_VALVE_ICON_URL} label="Air Valve" />
            </CardContent>
        </Card>
    );
}
